import { ColumnWidthUnits, CustomDataGrid, SettingsChangedActionType, SortType } from "@monza/toolkit";
import { BaseComponent } from "../../Core/BaseComponent/BaseComponent";
import { JSX } from "react";
import { TokenPager } from "@monza/toolkit/tables/paging/TokenPager";
import { ICustomizableDataGrid, IGridColumn, IGridRef, IPagingProps, IPagingResult } from "@monza/toolkit/tables/types";
import { TypeHelper } from "@iconics/core";
import ThemeProvider from "@monza/toolkit/theme/ThemeProvider";
import "./Styles.scss";
import DEFAULT_MONZO_THEME from "@monza/toolkit/theme/DEFAULT_THEME";
import { ITheme as IMonzoTheme } from "@monza/toolkit";
import React from "react";
import { Pagination } from "@serendie/ui";
import { createRoot } from "react-dom/client";
import { SerendieSymbol, SymbolName } from "@serendie/symbols";
import { Guid } from "@iconics/core";
import { CheckBox } from "../../Inputs/CheckBox/CheckBox";
import { GridContainer } from "../../Layout/Grid/GridContainer";
import { GridItem } from "../../Layout/Grid/GridItem";

export class DataGrid<TRow extends object> extends BaseComponent<IGridProps<TRow>, IGridState<TRow>>
{
    private static readonly DEFAULT_PAGE_SIZE = 20;
    private latestRequestId = 0;
    private mountId = '';
    private checkBoxColumnId = Guid.new().toString();
    private gridRef?: HTMLDivElement;

    constructor(props: IGridProps<TRow>)
    {
        super(props);
        this.state = {
            refreshToken: this.props.refreshToken,
            monzoTheme: DEFAULT_MONZO_THEME,
            currentPage: [],
        };
    }

    public async componentDidMount(): Promise<void>
    {
        this.mountId = Guid.new().toString();
        this.setState({ monzoTheme: this.getMonzoTheme() });
        await this.loadCurrentPage();
    }

    public async componentDidUpdate(prevProps: IGridProps<TRow>, prevState: IGridState<TRow>): Promise<void>
    {
        if (this.props.refreshToken != prevProps.refreshToken)
        {
            this.setState({ refreshToken: this.props.refreshToken });
            this.props.onChange({
                ...this.props.data,
                rows: [],
                pageNumber: 1,
                nextSkipToken: undefined,
                checkedIds: [],
            });
        }

        if (
            this.state.refreshToken != prevState.refreshToken ||
            this.hashRows(this.props.data.rows) != this.hashRows(prevProps.data.rows) ||
            this.props.data.sorter?.columnId != prevProps.data.sorter?.columnId ||
            this.props.data.sorter?.direction != prevProps.data.sorter?.direction ||
            (this.props.data.pageNumber ?? 1) != (prevProps.data.pageNumber ?? 1) ||
            this.props.data.nextSkipToken != prevProps.data.nextSkipToken ||
            this.pageSize != this.getPageSize(prevProps.paging?.pageSize)
        )
        {
            await this.loadCurrentPage();
        }
    }

    public getMonzoTheme(): IMonzoTheme
    {
        return {
            colors: {
                primary: {
                    // color: '', // pager button back
                    // foreground: '', // pager button fore
                },
                secondary: {
                    color: 'var(--colors-sd-system-color-component-inverse-primary)', // header back
                    foreground: 'var(--ui-text)', // header fore
                },
                surface: {
                    primary: 'var(--ui-background--alternate)', // row back
                    secondary: 'var(--ui-background)', // alternate row back
                    foregroundPrimary: 'var(--ui-text)', // row fore
                    border: 'var(--ui-line-break)', // row border
                },
                disabled: {
                    color: 'var(--ui-background)', // disabled pager button back
                    stroke: 'var(--ui-text)', // disabled pager button fore
                },
            },
        };
    }

    private hashRows(rows: TRow[]): string
    {
        return JSON.stringify(rows);
    }

    private getPageSize(pageSize?: number): number
    {
        return (pageSize || DataGrid.DEFAULT_PAGE_SIZE);
    }

    private get pageSize(): number
    {
        return this.getPageSize(this.props.paging?.pageSize);
    }

    private get lastPageNumber(): number
    {
        if (this.props.data.nextSkipToken == undefined || this.props.data.rows.length == 0)
        {
            return 1;
        }

        const lastKnownPageNumber = Math.floor((this.props.data.rows.length - 1) / this.pageSize) + 1;
        const lastPageNumber = (this.props.data.nextSkipToken == '' ? lastKnownPageNumber : lastKnownPageNumber + 1);
        return lastPageNumber;
    }

    private get columns(): IGridColumn<TRow>[]
    {
        let columns = this.props.columns.map(i => ({
            ...i,
            columnWidthUnits: i.columnWidthUnits ?? ColumnWidthUnits.PERCENTS,
        }));

        if (this.props.showCheckBoxes)
        {
            const checkedIds = this.props.data.checkedIds ?? [];

            columns = [
                {
                    columnId: this.checkBoxColumnId,
                    title: '',
                    getCellValue: row =>
                        <CheckBox
                            className='monza-data-grid__checkbox'
                            label=''
                            checked={checkedIds.includes(this.props.rowId(row))}
                            onClick={e => this.handleCheckBoxClick(row, e)}
                        />,
                    columnWidth: 40,
                    columnWidthUnits: ColumnWidthUnits.PIXELS,
                },
                ...columns
            ];
        }

        return columns;
    }

    private async loadCurrentPage(): Promise<void>
    {
        const skip = ((this.props.data.pageNumber ?? 1) - 1) * this.pageSize;
        const currentPage = this.props.data.rows.slice(skip, skip + this.pageSize);

        if (currentPage.length != this.pageSize && this.props.data.nextSkipToken != '')
        {
            await this.loadMoreRows();
            return;
        }

        this.setState({ currentPage: currentPage });
    }

    private async loadMoreRows(): Promise<void>
    {
        const request: IGridRowsRequest<TRow> = {
            skipToken: this.props.data.nextSkipToken ?? '',
            take: this.pageSize,
            sorter: this.props.data.sorter,
        };

        const requestId = ++this.latestRequestId;
        const mountId = this.mountId;
        let response: IGridRowsResponse<TRow> | undefined;

        try
        {
            response = await this.props.onRowsRequest(request);
        }
        catch (error)
        {
            console.log(error);
        }

        if (mountId != this.mountId || requestId !== this.latestRequestId)
        {
            return;
        }

        this.props.onChange({
            ...this.props.data,
            rows: [...this.props.data.rows, ...response?.rows ?? []],
            nextSkipToken: response?.nextSkipToken ?? '',
        });
    }

    private handlePagerClick(pageNumber: number): void
    {
        if (pageNumber < 1 || pageNumber > this.lastPageNumber)
        {
            return;
        }
        this.props.onChange({
            ...this.props.data,
            pageNumber: pageNumber,
        });
    }

    private handleGridSorterChange(...args: Parameters<NonNullable<ICustomizableDataGrid<TRow>['onGridSorterChanged']>>): void
    {
        const [sorters, actionType, affectedSorter, rowsFrom, rowsTo, setPage] = args;
        const sortColumnId = this.props.data.sorter?.columnId ?? '';
        const sortDirection = this.props.data.sorter?.direction ?? '';
        const newSortColumnId = affectedSorter.column?.columnId?.toString() ?? '';
        let sorter: IGridSorter | undefined;

        if (!newSortColumnId)
        {
            sorter = undefined;
        }
        else if (sortColumnId != newSortColumnId)
        {
            sorter = { columnId: newSortColumnId, direction: SortType.ASC };
        }
        else if (sortDirection == '')
        {
            sorter = { columnId: newSortColumnId, direction: SortType.ASC };
        }
        else if (sortDirection == SortType.ASC)
        {
            sorter = { columnId: newSortColumnId, direction: SortType.DESC };
        }
        else
        {
            sorter = undefined;
        }

        if (this.gridRef)
        {
            const columnIndex = this.columns.map(i => i.columnId).indexOf(newSortColumnId);
            this.gridRef.querySelectorAll(`th[role='columnheader']`).forEach(columnElement =>
            {
                const dataIndex = TypeHelper.toNumber(columnElement.getAttribute('data-index')) ?? -1;
                const directionElement = columnElement.querySelector('div:nth-child(2) > div:nth-child(2)');
                const column = this.columns[dataIndex];

                if (directionElement)
                {
                    let symbolName: SymbolName | '';
                    if (!column || !column.sortable)
                    {
                        symbolName = '';
                    }
                    else if (columnIndex != dataIndex || !sorter)
                    {
                        symbolName = 'chevron-up-down';
                    }
                    else if (sorter.direction == SortType.ASC)
                    {
                        symbolName = 'chevron-double-up';
                    }
                    else if (sorter.direction == SortType.DESC)
                    {
                        symbolName = 'chevron-double-down';
                    }
                    else
                    {
                        symbolName = 'chevron-up-down';
                    }

                    if (symbolName)
                    {
                        createRoot(directionElement).render(<SerendieSymbol name={symbolName} />);
                    }
                }
            });
        }

        this.props.onChange({
            ...this.props.data,
            rows: [],
            sorter: sorter,
            pageNumber: 1,
            nextSkipToken: undefined,
        });
    }

    private handleCellClick(...args: Parameters<NonNullable<typeof this.props.onCellClicked>>): void
    {
        const [item, column] = args;
        if (column.columnId == this.checkBoxColumnId)
        {
            return;
        }
        this.props.onCellClicked?.(...args);
    }

    private handleCheckBoxClick(row: TRow, event: React.MouseEvent<HTMLLabelElement, MouseEvent>): void
    {
        const rowId = this.props.rowId(row);
        const checkedIds = (this.props.data.checkedIds ?? []);
        const isChecked = !checkedIds.includes(rowId);
        let newCheckedIds: string[];

        if (this.props.allowOnlyOneCheckBox)
        {
            newCheckedIds = (isChecked ? [rowId] : []);
        }
        else if (isChecked)
        {
            newCheckedIds = [...checkedIds, rowId];
        }
        else
        {
            newCheckedIds = checkedIds.filter(i => i != rowId);
        }

        this.props.onChange({
            ...this.props.data,
            checkedIds: newCheckedIds,
        });
    }

    public render(): JSX.Element
    {
        const cssClasses: string[] = [];
        cssClasses.push('monza-data-grid');
        cssClasses.push(this.props.onCellClicked ? 'monza-data-grid--cell-clickable' : '');

        return (
            <GridContainer
                className={cssClasses.filter(i => !!i).join(' ')}
            >
                <GridItem
                    compact={12}
                >
                    <ThemeProvider
                        theme={this.state.monzoTheme}
                    >
                        <div
                            ref={i => { this.gridRef = i ?? undefined }}
                        >
                            <CustomDataGrid<TRow>
                                {...this.props}
                                columns={this.columns}
                                data={this.state.currentPage}
                                rowHeight={this.props.rowHeight ?? 30}
                                paging={{
                                    ...this.props.paging,
                                    pageSize: this.pageSize,
                                }}
                                onGridSorterChanged={(...args) => this.handleGridSorterChange(...args)}
                                sortOnlyByOneColumn={true}
                                onCellClicked={(...args) => this.handleCellClick(...args)}
                            >
                                <></>
                            </CustomDataGrid>
                        </div>
                    </ThemeProvider>
                </GridItem>
                <GridItem
                    compact={12}
                    style={{
                        paddingTop: '16px',
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Pagination
                        count={this.lastPageNumber}
                        page={this.props.data.pageNumber ?? 1}
                        onPageChange={i => this.handlePagerClick(i.page)}
                    />
                </GridItem>
            </GridContainer >
        );
    }

    public static toODataOrderBy(sorter?: IGridSorter): string
    {
        return (sorter ? `${sorter.columnId} ${sorter.direction.toLowerCase()}` : '');
    }

    public static sort<TRow extends object>(rows: TRow[], sorter?: IGridSorter): TRow[]
    {
        if (!sorter)
        {
            return rows;
        }

        const sortedRows = [...rows].sort((a, b) =>
        {
            const key = sorter.columnId as keyof TRow;
            const direction = (sorter.direction == SortType.ASC ? 1 : -1);
            const valueA = a[key];
            const normalisedValueA = (typeof valueA == 'string' ? valueA.toLowerCase() : valueA);
            const valueB = b[key];
            const normalisedValueB = (typeof valueB == 'string' ? valueB.toLowerCase() : valueB);
            const order = direction * (normalisedValueA < normalisedValueB ? -1 : 1);
            return order;
        });

        return sortedRows;
    }

    public static checkedIdsHash(checkedIds: string[]): string
    {
        return checkedIds.sort((a, b) => (a < b ? -1 : (a > b ? 1 : 0))).join(',');
    }
}

export interface IGridProps<TRow extends object> extends Omit<ICustomizableDataGrid<TRow>, 'data' | 'currentPage' | 'sorters' | 'sortOnlyByOneColumn' | 'onGridSorterChanged' | 'onGridGroupChanged' | 'onGridFilterChanged'>
{
    rowId: (row: TRow) => string;
    onRowsRequest: (request: IGridRowsRequest<TRow>) => Promise<IGridRowsResponse<TRow>>;
    onChange: (data: IGridData<TRow>) => void;
    data: IGridData<TRow>;
    refreshToken?: string;
    showCheckBoxes?: boolean;
    allowOnlyOneCheckBox?: boolean;
}

export interface IGridData<TRow extends object>
{
    rows: TRow[];
    sorter?: IGridSorter;
    pageNumber?: number;
    nextSkipToken?: string;
    checkedIds?: string[];
}

export interface IGridState<TRow extends object>
{
    refreshToken?: string;
    monzoTheme: IMonzoTheme;
    currentPage: TRow[];
}

export interface IGridRowsRequest<TRow extends object>
{
    skipToken: string;
    take: number;
    sorter?: IGridSorter;
}

export interface IGridRowsResponse<TRow extends object>
{
    nextSkipToken: string;
    rows: TRow[];
}

export interface IGridSorter
{
    columnId: string;
    direction: SortType;
}
